import React from 'react';
import { Trash2, Plus, User, Phone, Mail, Briefcase, Users } from 'lucide-react';

export const DynamicContactsTable = ({ contacts = [], onChange }) => {
  const handleAddRow = () => {
    const newEntry = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `contact-${window.crypto.randomUUID()}` : `contact-${Math.random().toString(36).slice(2, 9)}`,
      role: '',
      name: '',
      mobile: '',
      email: '',
      is_primary: 'No'
    };
    onChange([...contacts, newEntry]);
  };

  const handleRemoveRow = (indexToRemove) => {
    if (contacts.length <= 1) return;
    const filtered = contacts.filter((_, idx) => idx !== indexToRemove);
    onChange(filtered);
  };

  const handleFieldChange = (index, field, value) => {
    let sanitizedValue = value;
    if (field === 'mobile') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    }
    const updated = contacts.map((contact, idx) => {
      if (idx === index) {
        return { ...contact, [field]: sanitizedValue };
      }
      if (field === 'is_primary' && value === 'Yes') {
        return { ...contact, is_primary: 'No' };
      }
      return contact;
    });
    onChange(updated);
  };

  return (
    <div className="w-full">
      {/* Header with Title on Left and + Add row on Right */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 leading-tight">
              Key hospital contacts
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Add medical directors, emergency heads and administrative staff.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddRow}
          aria-label="Add new contact"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium cursor-pointer transition-colors shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add contact</span>
        </button>
      </div>

      <div className="space-y-5">
        {contacts.map((contact, idx) => (
          <div
            key={contact.id || idx}
            className="p-5 rounded-2xl bg-slate-50/70 border border-slate-200/80 relative"
          >
            {/* Entry Badge and Optional Delete */}
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white text-blue-600 border border-blue-200 shadow-2xs">
                Contact #{idx + 1}
                {contact.is_primary === 'Yes' && (
                  <span className="ml-1 text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                    Primary
                  </span>
                )}
              </span>

              {contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveRow(idx)}
                  aria-label={`Remove contact ${idx + 1}`}
                  className="inline-flex items-center gap-1 text-slate-400 hover:text-red-600 transition-colors px-2 py-1 rounded-md text-xs cursor-pointer"
                  title="Remove contact"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span className="text-[11px]">Remove</span>
                </button>
              )}
            </div>

            {/* Row 1: Role & Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Designation / Role
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Enter designation / role"
                    value={contact.role || ''}
                    onChange={(e) => handleFieldChange(idx, 'role', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Enter full name"
                    value={contact.name || ''}
                    onChange={(e) => handleFieldChange(idx, 'name', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Mobile & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="Enter 10-digit mobile number"
                    value={contact.mobile || ''}
                    onChange={(e) => handleFieldChange(idx, 'mobile', e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none transition-all ${
                      contact.mobile && contact.mobile.length > 0 && contact.mobile.length < 10
                        ? 'border-amber-400 focus:ring-1 focus:ring-amber-500 focus:border-amber-500'
                        : 'border-slate-200 focus:ring-1 focus:ring-blue-600 focus:border-blue-600'
                    }`}
                  />
                </div>
                {contact.mobile && contact.mobile.length > 0 && contact.mobile.length < 10 && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Must be exactly 10 digits ({contact.mobile.length}/10 entered)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={contact.email || ''}
                    onChange={(e) => handleFieldChange(idx, 'email', e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Primary Contact? */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Mark as Primary Point of Contact?
                </label>
                <select
                  value={contact.is_primary || ''}
                  onChange={(e) => handleFieldChange(idx, 'is_primary', e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 cursor-pointer"
                >
                  <option value="">Select option</option>
                  <option value="Yes">Yes (Direct Escalation)</option>
                  <option value="No">No (Secondary)</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DynamicContactsTable;
